import type { LabLeaderRow, LabResult } from './types'

/**
 * The models deliberately featured in the Pelican benchmark. There is no
 * catalog id for a bare "Claude Fable 5"; Fable 5.1 is the current model.
 * Keep this list as model IDs, not display labels, so renamed marketing copy
 * cannot silently change the benchmark cohort.
 */
export const FEATURED_BENCHMARK_MODELS = [
  'google/gemini-4-argon',
  'anthropic/claude-opus-5.5',
  'anthropic/claude-sonnet-5.5',
  'anthropic/claude-fable-5.1',
] as const

const media = (url: string | undefined) => Boolean(url && url.startsWith('/lab/media/'))

/** A real animated SVG gets display priority; a PNG-only "animated" result does not. */
export function isMovingSvg(result: LabResult): boolean {
  return result.kind === 'animated' && result.motion === 1 && media(result.svg)
}

function score(result: LabResult) { return result.score ?? -1 }

/**
 * Pick one result for a model. Prefer a verified moving SVG, then any animated
 * SVG, then a regular render. Within the same tier, the judge score wins.
 */
export function preferredResult(results: LabResult[]): LabResult | undefined {
  return [...results]
    .filter((r) => r.ok && (media(r.png) || media(r.svg)))
    .sort((a, b) => {
      // A frozen animation is a failed motion experiment, not a better showcase
      // than a completed Classic. It can only win when there is no render at all.
      const tier = (r: LabResult) => isMovingSvg(r) ? 3 : media(r.png) ? 2 : r.kind === 'animated' && media(r.svg) ? 1 : 0
      return tier(b) - tier(a) || score(b) - score(a) || Date.parse(b.created_at) - Date.parse(a.created_at)
    })[0]
}

/** Exactly the popular cohort, ranked by its real position in the full board. */
export function featuredRows(rows: LabLeaderRow[]): LabLeaderRow[] {
  const wanted = new Set<string>(FEATURED_BENCHMARK_MODELS)
  return rows.filter((r) => wanted.has(r.model)).sort((a, b) => a.rank - b.rank || (b.score ?? -1) - (a.score ?? -1))
}

/** One animated-first outcome for every ranked model, in leaderboard order. */
export function displayByModel(rows: LabLeaderRow[], results: LabResult[]): Map<string, LabResult> {
  const byModel = new Map<string, LabResult[]>()
  for (const result of results) byModel.set(result.model, [...(byModel.get(result.model) ?? []), result])
  const picked = new Map<string, LabResult>()
  for (const row of rows) {
    const result = preferredResult(byModel.get(row.model) ?? [])
    if (result) picked.set(row.model, result)
  }
  return picked
}
